import { Subscription } from 'rxjs';
import { Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ArrHlp } from 'src/lib/fliko/helpers/array.helper';
import { ObjHlp } from 'src/lib/fliko/helpers/object.helper';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { EstateData, EstateType } from 'src/lib/fliko/models/estate/estate.data';
import { LandlordType, ResidentData, ResidentType } from 'src/lib/fliko/models/person/resident.data';
import { ContextDataService } from 'src/app/view/user-context/services/context.data-service';
import { ErrorHandlingService } from '../../error-handling/error-handling.service';
import { GrammarHelper } from '../../helpers/grammar.helper';
import { LocalizationHelper } from '../../helpers/localization.helper';
import { GusDataSimple } from '../../integrations/gus-api/gus.data';
import { SelectInputData } from '../../interfaces/select-input-data.interface';
import { DialogService } from '../../services/dialog.service';
import { LocalizationService } from '../../services/localization.service';
import { PushIdService } from '../../services/pushId.service';
import { ControlValidators } from '../../validators/control.validators';
import { GroupValidators } from '../../validators/group.validators';
import { UserCommunicationFormComponent } from '../user-communication-form/user-communication-form.component';

@Component({
  selector: 'app-person-edit-form',
  templateUrl: './person-edit-form.component.html',
  styleUrls: ['./person-edit-form.component.scss']
})
export class PersonEditFormComponent implements OnChanges, OnDestroy {
  @Input() residentData: ResidentData | null = null;
  @Input() residentType: ResidentType = ResidentType.LANDLORD;
  @Input() create = false;
  @Input() disabled = false;
  @Input() showEmergencyContact = true;
  @Input() outsider = false;

  public mainForm!: FormGroup;
  public addressForm!: FormGroup;
  public emergencyForm!: FormGroup;
  public useAddress = false;
  public useEmergencyContact = false;

  public companyControl!: FormControl;
  public landlordTypes: SelectInputData<string>[] = [];

  public estate: EstateData | null = null;

  public editPersonalControl = new FormControl(false);
  public get disablePersonal() { return !this.editPersonalControl.value; } // prettier-ignore

  EstateType = EstateType;
  ResidentType = ResidentType;
  GrammarHelper = GrammarHelper;

  public get userEmails(): string[] {
    return ArrHlp.make(this.residentData?.email);
  }

  public get userPhones(): string[] {
    return ArrHlp.make(this.residentData?.phone);
  }

  public getResidentTypeKey(caseShort: string): string {
    return GrammarHelper.getResidentKeyInCase(this.estate?.type ?? EstateType.COMMUNITY, this.residentType, caseShort);
  }

  public get isCompany(): boolean {
    return this.companyControl.value == LandlordType.COMPANY;
  }

  private subscription$: Subscription = new Subscription();

  @ViewChild('emailForm') emailForm!: UserCommunicationFormComponent;
  @ViewChild('phoneForm') phoneForm!: UserCommunicationFormComponent;

  constructor(
    public errorHandlingService: ErrorHandlingService,
    private ctxDS: ContextDataService,
    private fb: FormBuilder,
    private dialogService: DialogService,
    private ls: LocalizationService,
    private pushId: PushIdService
  ) {
    this.createForms();

    const lh = new LocalizationHelper(this.ls);

    this.subscription$.add(
      this.ls.onLanguageChanged$.subscribe(() => {
        // prettier-ignore
        this.landlordTypes = lh.localizeTitle([
          { value: LandlordType.PERSON,   title: 'user.person' },
          { value: LandlordType.COMPANY,  title: 'label.firm' }
        ]);
      })
    );

    this.subscription$.add(this.ctxDS.currentEstate$.subscribe((estate) => (this.estate = estate!)));

    this.subscription$.add(
      this.editPersonalControl.valueChanges.subscribe((value) => {
        if (value) {
          const t = (x: string) => StrHlp.capitalize(this.ls.translate(x));
          // prettier-ignore
          this.dialogService.openConfirmationDialog({
              text: t('guard.editResident' + (this.estate?.type == EstateType.PRS ? 'PRS' : 'Comm')) + ' ' +
                t('guard.editResident'),
              acceptKey:'label.yes',
              cancelKey:'label.no'
          })
          .subscribe((answer) => {
            if (!answer) this.editPersonalControl.patchValue(false, { emitEvent: false });
          });
        }
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    const residentChanges = changes.residentData;
    if (residentChanges && residentChanges.currentValue) {
      this.patchForms();
    }
    if (changes.create) this.editPersonalControl.patchValue(changes.create.currentValue, { emitEvent: false });
  }

  ngOnDestroy(): void {
    this.subscription$.unsubscribe();
  }

  public updateValidators(): void {
    const allFields = ['NIP', 'REGON', 'lastName'];
    let requiredFields = [];
    if (this.isCompany) {
      requiredFields = ['NIP', 'REGON'];
    } else {
      requiredFields = ['lastName'];
    }

    for (const key of allFields) {
      const control = this.mainForm.get(key);
      if (requiredFields.includes(key)) {
        control?.addValidators([Validators.required]);
        if (key == 'NIP') control?.addValidators([ControlValidators.nipValidator()]);
      } else {
        control?.clearValidators();
      }
      control?.updateValueAndValidity();
    }
  }

  public resolveGusData(data: GusDataSimple): void {
    this.mainForm.patchValue({ REGON: data.REGON, name: data.name });
    this.addressForm.patchValue({
      street: data.street,
      number: data.number,
      city: data.city,
      country: data.country,
      zipCode: data.zipCode
    });
  }

  /**
   * Check validity of forms and returns true if invalid
   * USED IN PARENT COMPONENTS
   */
  public get invalid(): boolean {
    let invalid = false;
    if (this.mainForm.invalid || this.emailForm.invalid || this.phoneForm.invalid) {
      invalid = true;
      this.mainForm.markAllAsTouched();
    }
    if (this.useAddress && this.addressForm.invalid) {
      invalid = true;
      this.addressForm.markAllAsTouched();
    }

    if (this.useEmergencyContact && this.emergencyForm.invalid) {
      invalid = true;
      this.emergencyForm.markAllAsTouched();
    }
    return invalid;
  }

  /**
   * USED IN PARENT COMPONENTS
   */
  public summaryData(): ResidentData | null {
    if (this.invalid) {
      return null;
    }
    const estateId = this.estate?.id;
    const email = this.emailForm.summaryData();
    const phone = this.phoneForm.summaryData();
    const now = Date.now();

    const address = ObjHlp.sanitizeForDB(this.addressForm.value);
    const contact = ObjHlp.sanitizeForDB(this.emergencyForm.value);
    const birthDate = this.mainForm.controls.birthDate.value;

    let paramsToCompare = {
      id: this.create ? `${estateId}_${this.pushId.create()}` : this.residentData!.id,
      estateId,
      ...this.mainForm.value,
      phone,
      email,
      birthDate: birthDate ? new Date(birthDate).valueOf() : null,
      address: this.useAddress ? address : null,
      emergencyContact: this.useEmergencyContact ? contact : null,
      lastUpdate: now
    };

    let additionalParams: any = {};
    if (this.residentType == ResidentType.LANDLORD) {
      additionalParams = { type: this.companyControl.value };
      if (additionalParams.type == LandlordType.COMPANY) {
        additionalParams = {
          ...additionalParams,
          NIP: this.mainForm.controls.NIP.value,
          REGON: this.mainForm.controls.REGON.value
        };
      }
    }
    paramsToCompare = { ...paramsToCompare, ...additionalParams };
    const residentChanged =
      // ? For check purposes lastUpdate is set to the same value as in paramsToCompare and userId is removed,
      // because it's not set in this form
      this.create ||
      !ObjHlp.deepCompare(paramsToCompare, ObjHlp.replaceNullish({ ...this.residentData, lastUpdate: now, userId: undefined }));
    const finalParams = {
      ...paramsToCompare,
      lastUpdate: residentChanged ? now : (this.residentData?.lastUpdate ?? now),
      // ? Clear last name if person changes to company
      ...(paramsToCompare.type == LandlordType.COMPANY ? { lastName: null } : {})
    };
    return ObjHlp.replaceEmptyStr(finalParams, null);
  }

  public showDialog(field: 'useEmergencyContact' | 'useAddress', value: boolean): void {
    if (!value) {
      const t = (x: string) => StrHlp.capitalize(this.ls.translate(x));
      this.dialogService
        .openConfirmationDialog({
          text: t(`guard.remove${field == 'useEmergencyContact' ? 'Contact' : 'Address'}`),
          acceptKey: 'label.yes',
          cancelKey: 'label.no'
        })
        .subscribe((result) => (this[field] = !result));
    }
  }

  // USED IN PARENT COMPONENT
  public get dirty(): boolean {
    return (
      this.companyControl.dirty ||
      this.mainForm.dirty ||
      this.addressForm.dirty ||
      this.emailForm.dirty ||
      this.phoneForm.dirty ||
      this.emergencyForm.dirty ||
      this.useEmergencyContact != !!this.residentData?.emergencyContact ||
      this.useAddress != !!this.residentData?.address
    );
  }

  private createForms(): void {
    this.mainForm = this.fb.group({
      name: [null, Validators.required],
      lastName: null,
      ownerCode: null,
      PESEL: null,
      documentNo: null,
      nationality: null,
      NIP: null,
      REGON: null,
      birthDate: null
    });
    this.addressForm = this.fb.group({
      street: [null, Validators.required],
      number: [null, Validators.required],
      city: [null, Validators.required],
      country: [null],
      zipCode: [null, [Validators.required, ControlValidators.zipCodeValidator()]]
    });
    this.emergencyForm = this.fb.group(
      {
        name: [null, Validators.required],
        lastName: [null, Validators.required],
        email: [null, Validators.email],
        phone: [null]
      },
      { validators: [GroupValidators.requiredAnyOf(['email', 'phone'], ['label.phone', 'label.email'])] }
    );

    this.companyControl = this.fb.control('PERSON');
    this.subscription$.add(
      this.companyControl.valueChanges.subscribe((value) => {
        this.useAddress = value == LandlordType.COMPANY;
        this.updateValidators();
      })
    );
  }

  private patchForms(): void {
    if (this.residentData && !this.create) {
      this.companyControl.patchValue(
        this.residentType == ResidentType.LANDLORD && !!this.residentData.NIP ? LandlordType.COMPANY : LandlordType.PERSON
      );

      this.mainForm.patchValue({
        name: this.residentData.name,
        lastName: this.residentData.lastName,
        ownerCode: this.residentData.ownerCode ?? null,
        birthDate: this.residentData.birthDate ? new Date(this.residentData.birthDate) : null,
        PESEL: this.residentData.PESEL ?? null,
        documentNo: this.residentData.documentNo ?? null,
        nationality: this.residentData.nationality ?? null
      });

      if (this.companyControl.value == LandlordType.COMPANY) {
        this.mainForm.patchValue({ NIP: this.residentData.NIP ?? '', REGON: this.residentData.REGON ?? '' });
      }
      this.useAddress = !!this.residentData.address;
      this.addressForm.patchValue(this.residentData.address ?? {});

      const contact = this.residentData.emergencyContact;
      this.useEmergencyContact = !!contact;
      if (contact) {
        this.emergencyForm.patchValue(ObjHlp.replaceEmptyStr(contact, null));
      }
      this.updateValidators();
    }
  }
}
